import { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { Loader2, History } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useModalStack } from '@/context/ModalStackContext';
import { EventService } from '@/services/EventService';
import { EventChangeLogEntry } from '@/types';

interface EventChangeLogDialogProps {
  eventId: string | null;
  open: boolean;
  onClose: () => void;
}

const operationLabels: Record<string, string> = {
  EVENT_UPDATE: 'עדכון תיעוד',
  EVENT_DRAFT_UPDATE: 'עדכון טיוטה',
  EVENT_PUBLISH: 'פרסום תיעוד',
  PUBLIC_SHARE_ANSWER: 'מענה מקישור ציבורי',
};

const fieldLabels: Record<string, string> = {
  type: 'סוג תיעוד',
  date: 'תאריך',
  text: 'תוכן',
  status: 'סטטוס',
  answer: 'תשובה',
};

const actionLabels: Record<string, string> = {
  VALUE_ADDED: 'נוסף ערך',
  VALUE_CHANGED: 'שונה ערך',
  VALUE_DELETED: 'נמחק ערך',
};

const PAGE_SIZE = 50;

// null/undefined/empty → "—"; objects → JSON; else the raw string.
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const getActorLabel = (entry: EventChangeLogEntry): string => {
  if (entry.performedByDisplayName) return entry.performedByDisplayName;
  if (entry.performedByType === 'GUEST') return 'אורח';
  if (entry.performedByType === 'SYSTEM') return 'מערכת';
  return '';
};

export default function EventChangeLogDialog({ eventId, open, onClose }: EventChangeLogDialogProps) {
  const modalId = useRef(`event-changelog-modal-${Date.now()}`).current;
  const { registerModal } = useModalStack();

  const [items, setItems] = useState<EventChangeLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  // רישום החלון במחסנית החלונות הקופצים (Register modal in stack)
  useEffect(() => {
    if (!open || !eventId) return;
    return registerModal({
      id: modalId,
      priority: 1000,
      onClose,
    });
  }, [open, eventId, registerModal, modalId, onClose]);

  // Fetch page 1 when opened; reset when closed so a reopen for another event refetches.
  useEffect(() => {
    if (!open || !eventId) {
      setItems([]);
      setPage(1);
      setTotal(0);
      setError(false);
      setIsLoading(false);
      setIsLoadingMore(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    EventService.getEventChangeLogs(eventId, 1, PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setPage(res.page);
        setTotal(res.total);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, eventId]);

  const handleLoadMore = useCallback(async () => {
    if (!eventId) return;
    setIsLoadingMore(true);
    try {
      const res = await EventService.getEventChangeLogs(eventId, page + 1, PAGE_SIZE);
      setItems((prev) => [...prev, ...res.items]);
      setPage(res.page);
      setTotal(res.total);
    } catch {
      setError(true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [eventId, page]);

  const hasMore = items.length < total;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <History className="w-5 h-5" />
            לוג שינויים
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-muted-foreground">
            לא ניתן לטעון את לוג השינויים כרגע
          </div>
        ) : total === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            אין עדיין לוג שינויים לתיעוד הזה
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((entry) => (
              <div
                key={entry.changeSetId}
                className="bg-card rounded-xl p-4 border border-border shadow-soft"
              >
                <div className="flex items-center justify-between mb-2 gap-2">
                  <span className="text-sm font-medium">
                    {operationLabels[entry.operation] ?? entry.operation}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(entry.createdAt), 'd בMMMM yyyy, HH:mm', { locale: he })}
                  </span>
                </div>

                <div className="flex items-center justify-between mb-3 gap-2">
                  <span className="text-xs text-muted-foreground">{getActorLabel(entry)}</span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary">
                    גרסה {entry.entityVersion}
                  </span>
                </div>

                {/* Change reason — change-set audit metadata, not a tracked field. Hidden entirely when null (historical entries). */}
                {entry.changeReason != null && entry.changeReason !== '' && (
                  <div className="mb-3 pt-2 border-t border-border">
                    <div className="text-xs font-medium text-muted-foreground mb-1">מהות השינוי</div>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                      {entry.changeReason}
                    </p>
                  </div>
                )}

                {entry.changes.length > 0 && (
                  <ul className="space-y-2">
                    {entry.changes.map((change, idx) => (
                      <li
                        key={`${entry.changeSetId}-${change.fieldName}-${idx}`}
                        className="text-sm border-t border-border pt-2 first:border-t-0 first:pt-0"
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-medium">
                            {fieldLabels[change.fieldName] ?? change.fieldName}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {actionLabels[change.actionType] ?? change.actionType}
                          </span>
                        </div>
                        <div className="text-muted-foreground break-words">
                          {change.actionType === 'VALUE_CHANGED' ? (
                            <span>
                              {formatValue(change.oldValue)} <span className="mx-1">←</span>{' '}
                              {formatValue(change.newValue)}
                            </span>
                          ) : change.actionType === 'VALUE_ADDED' ? (
                            <span>{formatValue(change.newValue)}</span>
                          ) : (
                            <span>{formatValue(change.oldValue)}</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button variant="outline" onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
                  טען עוד
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
